import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

const BUDGETS = ["< $25k", "$25k – $75k", "$75k – $200k", "$200k+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and a short message.");
      return;
    }
    setSubmitting(true);
    // Mock submission — pretend to send to an API
    await new Promise((r) => setTimeout(r, 1100));
    setSubmitting(false);
    setDone(true);
    toast.success("Brief received. We'll be in touch within 24 hours.");
  };

  return (
    <section
      id="contact"
      data-testid={TID.contact.section}
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-hairline-strong to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="md:col-span-6"
        >
          <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
            ◇ Contact
          </span>
          <h2 className="mt-4 font-display text-6xl md:text-8xl font-bold tracking-[-0.04em] leading-[0.9] text-primary-x">
            Let&apos;s
            <br />
            <em className="not-italic bg-gradient-to-br from-primary-x via-secondary-x to-muted-x bg-clip-text text-transparent">
              build it.
            </em>
          </h2>
          <p className="mt-8 text-secondary-x max-w-md leading-relaxed">
            Tell us about the product, system, or threat you&apos;d like us to help
            with. We respond to every brief within 24 hours.
          </p>

          <div className="mt-12 grid grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-2">
                Email
              </div>
              <a
                href="mailto:hello@ulties.com"
                className="text-primary-x hover:text-secondary-x transition-colors"
              >
                hello@ulties.com
              </a>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-2">
                Studios
              </div>
              <div className="text-primary-x">Remote · Lisbon · Bengaluru</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="md:col-span-6"
        >
          <form
            onSubmit={onSubmit}
            className="relative rounded-2xl border border-hairline bg-glass backdrop-blur-2xl p-6 md:p-10 glow-ring"
          >
            {done ? (
              <div
                data-testid={TID.contact.success}
                className="flex flex-col items-start gap-4 py-8"
              >
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-lg" style={{ background: "var(--text-primary)", color: "var(--canvas)" }}>
                  ✓
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-primary-x">
                  Brief received.
                </h3>
                <p className="text-secondary-x">
                  Thanks {form.name?.split(" ")[0] || "—"}, we&apos;ll be in touch
                  within 24 hours from{" "}
                  <span className="text-primary-x">hello@ulties.com</span>.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", company: "", budget: "", message: "" });
                  }}
                  className="mt-4 text-sm text-secondary-x underline-offset-4 hover:underline"
                >
                  Send another brief
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Your name" required>
                  <input
                    data-testid={TID.contact.name}
                    type="text"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Jane Doe"
                    className="input-x"
                  />
                </Field>
                <Field label="Work email" required>
                  <input
                    data-testid={TID.contact.email}
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="jane@company.com"
                    className="input-x"
                  />
                </Field>
                <Field label="Company" className="md:col-span-1">
                  <input
                    data-testid={TID.contact.company}
                    type="text"
                    value={form.company}
                    onChange={onChange("company")}
                    placeholder="Acme Inc."
                    className="input-x"
                  />
                </Field>
                <Field label="Budget" className="md:col-span-1">
                  <select
                    data-testid={TID.contact.budget}
                    value={form.budget}
                    onChange={onChange("budget")}
                    className="input-x appearance-none pr-10"
                  >
                    <option value="">Select range</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b} className="bg-surface-2 text-primary-x" style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
                        {b}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Tell us about the project" required className="md:col-span-2">
                  <textarea
                    data-testid={TID.contact.message}
                    rows={5}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="What are you building, and what's the hardest part?"
                    className="input-x resize-none"
                  />
                </Field>
                <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
                  <p className="text-xs text-muted-x">
                    By submitting you agree to our minimal use of this data to
                    reply to your enquiry.
                  </p>
                  <button
                    type="submit"
                    data-testid={TID.contact.submit}
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                    style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
                  >
                    {submitting ? "Sending…" : "Send Brief"}
                    <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </motion.div>
      </div>

      <style>{`
        .input-x {
          width: 100%;
          background: var(--glass);
          border: 1px solid var(--hairline-strong);
          border-radius: 12px;
          padding: 12px 14px;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
          transition: border-color .2s, background .2s;
        }
        .input-x::placeholder { color: var(--text-muted); }
        .input-x:focus { border-color: var(--text-primary); background: var(--glass-hover); }
      `}</style>
    </section>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x mb-2">
        {label} {required && <span className="text-primary-x">*</span>}
      </span>
      {children}
    </label>
  );
}
